import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomButton } from '../../../../../../utils/Button';
import { formatCurrency } from '../../../../../../utils/FormatCurrency';
import {
  createPaystackMandate,
  createPaystackOrder,
  getAllLinkedAccounts,
  getOrderDetails,
} from '../../../../../../api/orderAPI';
import { consolidateCartPayments } from '../../../../../../utils/ConsolidateCartPayment';
import { Link, useNavigate } from 'react-router-dom';
import { GlobalMandateTermsModal } from './directDebitMandateModals/GlobalMandateTermsModal';
import { toast } from 'react-toastify';
import { setPaystackOrderReference } from '../../../../../../features/order/fullPaymentSlice';
import { clearCart } from '../../../../../../features/cart/cartSlice';
import { LinkedAccounts } from './LinkedAccounts';
import { DebitDirectMandateTermsModal } from './directDebitMandateModals/DebitDirectMandateTermsModal';
import { WhatIsDirectDebit } from './directDebitMandateModals/WhatIsDirectDebit';

export const MakeDirectDebit = () => {
  const mandateData = useSelector((state) => state.mandate.data);
  const cart = useSelector((state) => state.cart.cart);
  const consolidatedPayments = consolidateCartPayments(cart);
  const [isGlobalModalOpen, setGlobalModalOpen] = useState(false);
  const [isDirectDebitModalOpen, setDirectDebitModalOpen] = useState(false);
  const [isWhatIsDirectDebitOpen, setIsWhatIsDirectDebitOpen] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const dispatch = useDispatch();
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedBankAuthCode, setSelectedBankAuthCode] = useState(null);

  const { data: linkedAccounts } = useQuery({
    queryKey: ['linkedAccounts'],
    queryFn: () => getAllLinkedAccounts(),
  });

  useEffect(() => {
    if (isGlobalModalOpen || isDirectDebitModalOpen || isWhatIsDirectDebitOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = 'auto';
  }
}, [isGlobalModalOpen, isDirectDebitModalOpen, isWhatIsDirectDebitOpen]);

  const directDebitPaymentFrequency =
    consolidatedPayments?.otherPayments.length;

  const downPaymentSuccess = useSelector(
    (state) => state.fullPayment.downPaymentSuccess
  );
  const paystackOrderReference = useSelector(
    (state) => state.fullPayment.paystackOrderReference
  );

  const navigate = useNavigate();

  const handleDisplayGlobalMandateModals = () => {
    setGlobalModalOpen((prev) => !prev);
  };
  const handleDisplayDirectDebitModal = () => {
    setDirectDebitModalOpen((prev) => !prev);
  };
  const handleDisplayWhatIsDirectDebit = () => {
    setIsWhatIsDirectDebitOpen((prev) => !prev);
  }

  const shouldIncludeAuthCode =
    linkedAccounts?.has_existing_mandates && selectedAccountId;

  const addAnotherBank =
    linkedAccounts?.can_add_new && linkedAccounts?.has_existing_mandates;

  const { mutate: createMandate, isPending: isValidating } = useMutation({
    mutationFn: () => {
      let mandateParams = { reference: paystackOrderReference };

      if (shouldIncludeAuthCode) {
        // Case 1: Include auth code
        mandateParams = {
          reference: paystackOrderReference,
          authorization_code: selectedBankAuthCode,
        };
      } else if (addAnotherBank) {
        // Case 2: Add another bank mandate
        mandateParams = {
          reference: paystackOrderReference,
          link_new_mandate: true,
        };
      } else if (!shouldIncludeAuthCode && !addAnotherBank) {
        mandateParams = {
          reference: paystackOrderReference,
        };
      }

      return createPaystackMandate(mandateParams);
    },

    onSuccess: (res) => {
      setOrderId(res?.data?.master_order_id);
      const redirectUrl = res.data?.redirect_url;

      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      if (res.success === true) {
        navigate(
          `/cart-items/checkout/direct-debit/success?orderid=${res?.data?.order_details?.orderNumber}`
        );

        dispatch(setPaystackOrderReference(null));
      }
    },
    onError: (error) => {
      toast.error(
        error?.data?.message || 'Failed to create mandate. Please try again.'
      );
    },
  });

  const handleCreateMandate = () => {
    if (!downPaymentSuccess) return;
    createMandate();
  };


  return (
    <>
      <section className="w-full">
        <p className="text-sm mb-4 font-semibold">
          <span className="mr-1 font-normal">Step 2.</span>
          Set up direct debit mandate for your upcoming installments
        </p>
        <div
          className={`w-ful overflow-cli rounded-xl border p-4 lg:p-6 py-8 shadow-sm flex flex-col justify-center items-center
        ${downPaymentSuccess ? 'bg-white border-[#DEDEDE]' : 'bg-[#FFFFFF]  text-[#D9D9D9] cursor-not-allowed'}
      `}
        >
          <p className="text-lg font-semibold  ">DIRECT DEBIT</p>
          <div className="border border-[#DEDEDE] w-full mx-10 mt-3 mb-4"></div>
          {linkedAccounts?.has_existing_mandates && (
            <article className="justify-center items-center flex flex-col ">
              <p className="text-xs mb-2">
                Select your linked bank account to continue
              </p>
              <LinkedAccounts
                linkedAccounts={linkedAccounts}
                downPaymentSuccess={downPaymentSuccess}
                selectedAccountId={selectedAccountId}
                setSelectedAccountId={setSelectedAccountId}
                setSelectedBankAuthCode={setSelectedBankAuthCode}
              />
              {linkedAccounts?.can_add_new && (
                <button
                  onClick={handleCreateMandate}
                  className={`${downPaymentSuccess ? 'cursor-pointer' : 'cursor-not-allowed'} text-sm underline font-duplet font-medium mb-6`}
                  disabled={!downPaymentSuccess}
                >
                  Link another bank account
                </button>
              )}
            </article>
          )}
          <p className="font-duplet text-sm font-normal mb-3">
            Total Installment Amount
          </p>
          <p className="  font-semibold text-3xl mb-4">
            {formatCurrency(
              mandateData?.consolidated_total_amount -
                mandateData?.first_installment_payment
            )}
          </p>
          <p className="font-duplet text-sm font-normal mb-3">
            Number of Installment
          </p>
          <p className="  font-semibold text-3xl mb-4">
            {directDebitPaymentFrequency}
          </p>

          {linkedAccounts?.has_existing_mandates && (
            <div className="w-full md:w-2/3">
              <CustomButton
                width="100%"
                text={isValidating ? 'Creating...' : 'Continue'}
                onClick={handleCreateMandate}
                disabled={
                  isValidating || !downPaymentSuccess || !selectedAccountId
                }
                bgColor={
                  downPaymentSuccess ? 'var(--yellow-primary)' : '#F6F6F6'
                }
                className={!downPaymentSuccess && 'text-[#E8EBEA] '}
              />
            </div>
          )}

          {!linkedAccounts?.has_existing_mandates && (
            <div className="w-full md:w-2/3">
              <CustomButton
                width="100%"
                text={isValidating ? 'Setting up...' : 'Set up Direct Debit'}
                onClick={handleCreateMandate}
                disabled={isValidating || !downPaymentSuccess}
                bgColor={
                  downPaymentSuccess ? 'var(--yellow-primary)' : '#F6F6F6'
                }
                className={!downPaymentSuccess && 'text-[#E8EBEA] '}
              />
            </div>
          )}

          <p className="font-light text-[11px] text-center mt-6 mb-2 mx-8 w-[317px] text-balance">
            By clicking Setup direct debit, you agree to our{' '}
            <br className="hidden mb:block" />
            <span
              className={`${downPaymentSuccess ? 'cursor-pointer' : 'cursor-not-allowed'} font-bold underline`}
              onClick={() => {
                handleDisplayDirectDebitModal(true);
              }}
            >
              Direct debit terms of use,
            </span>{' '}
            <span
              className={`${downPaymentSuccess ? 'cursor-pointer' : 'cursor-not-allowed'} font-bold underline`}
              onClick={() => {
                handleDisplayGlobalMandateModals(true);
              }}
            >
              Global standing mandate terms of use
            </span>
            and{' '}
            <Link
              target="_blank"
              to="https://paystack.com/privacy/merchant"
              className={`${downPaymentSuccess ? 'cursor-pointer' : 'cursor-not-allowed'} font-bold underline`}
            >
              Paystack’s end-user policy
            </Link>
          </p>
        
          
          <span
              className={`${downPaymentSuccess ? 'cursor-pointer' : 'cursor-not-allowed'} underline text-sm`}
              onClick={() => {
                handleDisplayWhatIsDirectDebit(true);
              }}
            >
              What is direct debit?
            </span>{' '}
            

        </div>
      </section>
      <GlobalMandateTermsModal
        isOpen={isGlobalModalOpen}
        onRequestClose={() => handleDisplayGlobalMandateModals(false)}
      />
      <DebitDirectMandateTermsModal
        isOpen={isDirectDebitModalOpen}
        onRequestClose={() => handleDisplayDirectDebitModal(false)}
      />
      <WhatIsDirectDebit
        isOpen={isWhatIsDirectDebitOpen}
        onRequestClose={() => handleDisplayWhatIsDirectDebit(false)}
      />
    </>
  );
};
